import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { tenantApiFetch } from './tenantApi';

export default function TenantFeaturesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch('/api/tenant/features');
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setData(json);
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return <Alert severity="error">{error}</Alert>;
  }

  const features = data?.features || [];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Features
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Active capabilities enabled for your organization (global rollout, your plan, or a tenant-specific override).
      </Typography>
      {data?.resolution_note && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {data.resolution_note}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {features.map((f) => (
              <TableRow key={f.id}>
                <TableCell>{f.feature_name}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {f.feature_code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip size="small" label={f.source || '—'} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {f.description || '—'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {!features.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="text.secondary">No active features resolved for your tenant.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
